/**
 * The stage's measurement. Only `useStageLayout` writes it, inside the `commitBeforePaint` of the
 * change, so its readers paint in the same frame.
 */

import { atom } from 'jotai';
import { atomWithLazy } from 'jotai/utils';

import type { StageLayout } from '@/types/panels';

import { calcStageLayout, FALLBACK_STAGE_WIDTH_PX } from './shellStage';

const layoutStateAtom = atomWithLazy((): StageLayout => {
    const width = typeof window !== 'undefined' ? window.innerWidth : FALLBACK_STAGE_WIDTH_PX;
    return calcStageLayout(width, width);
});

const resizingStateAtom = atom(false);

// Read-only: the write atoms below are the only writers.
export const stageWidthAtom = atom((get): number => get(layoutStateAtom).stageWidth);

// The window is narrower than the phone breakpoint, not the stage.
export const isSmViewportAtom = atom((get): boolean => get(layoutStateAtom).isPhone);

export const isStageResizingAtom = atom((get): boolean => get(resizingStateAtom));

/** Keeps the old object when nothing changed, so no reader wakes. */
export const setStageLayoutAtom = atom(null, (get, set, layout: StageLayout): void => {
    const previous = get(layoutStateAtom);
    if (layout.stageWidth === previous.stageWidth && layout.isPhone === previous.isPhone) {
        return;
    }
    set(layoutStateAtom, layout);
});

export const setStageResizingAtom = atom(null, (_get, set, isResizing: boolean): void => {
    set(resizingStateAtom, isResizing);
});
